import {isEqual} from 'lodash-es';
import React, {FC, useState} from 'react';

import {EditableFormTableContext} from 'components/editable-table/model/editable-form-table.context';
import {ModalCloseConfirmation} from 'components/modal-close-confirmation';
import {selectEditableTableEntityData} from 'modules/data/data-selectors';
import {ITableEntityData} from 'modules/data/data-types';
import {useAppSelector} from 'store/config/hooks';
import {TModalComponentInterface, selectModalData} from 'store/slices/modals-slice';

import {useEditableTableActions} from '../hooks/use-editable-table-actions';
import {useEditableTableColumns} from '../hooks/use-editable-table-colums';
import {EditableTableModal} from './editable-table-modal';

export const EditableTableModalContainer: FC<TModalComponentInterface> = ({
    onClose,
}: TModalComponentInterface) => {
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const entityName = useAppSelector(selectModalData)?.entityName || '';
    const data: ITableEntityData | undefined = useAppSelector(selectEditableTableEntityData(entityName));
    const initialRows = data?.initialRows;
    const {
        width,
        modalTitle,
        initData,
        form,
        setForm,
        validation,
        setValidation,
        tableData,
        actions,
        handleEdit,
        disabledPagination,
    } = useEditableTableActions(entityName);

    const {columnsMeta} = useEditableTableColumns(entityName);

    const handleCancel = () => {
        if (initData.length && columnsMeta && initialRows) {
            const fieldKeys = columnsMeta?.map(column => column.key);
            const isDirty = initData.some(record => {
                const initialRow = initialRows.find(initRow => initRow.id === record.id);
                return initialRow ? fieldKeys.some(key => !isEqual(initialRow[key], record[key])) : true;
            });
            if (isDirty) {
                setIsConfirmVisible(true);
                return;
            }
        }
        onClose();
    };

    return (
        <>
            <EditableFormTableContext.Provider
                value={{
                    initData,
                    form,
                    setForm,
                    validation,
                    setValidation,
                }}
            >
                <EditableTableModal
                    width={width}
                    disabledPagination={disabledPagination}
                    entityName={entityName}
                    modalTitle={modalTitle}
                    onCancel={handleCancel}
                    onClose={onClose}
                    handleEdit={handleEdit}
                    columns={columnsMeta}
                    data={tableData || []}
                    actions={actions}
                />
            </EditableFormTableContext.Provider>
            {isConfirmVisible && (
                <ModalCloseConfirmation
                    isOpen={isConfirmVisible}
                    onClose={() => {
                        onClose();
                        setIsConfirmVisible(false);
                    }}
                    onBack={() => {
                        setIsConfirmVisible(false);
                    }}
                />
            )}
        </>
    );
};
